import React, { useEffect, useState } from 'react';
import { XMarkIcon, DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, TruckIcon, CubeIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { XmlCte } from '../../types/cte';
import { useBaixarXmlCte } from '../../hooks/useCte';
import { money, formatCnpj, formatTipoFrete, formatDateTime } from '../../utils/format';
import { btnPrimario, btnSecundario } from '../ui/iris';

interface CteModalProps {
  cte: XmlCte | null;
  isOpen: boolean;
  onClose: () => void;
}

const CteModal: React.FC<CteModalProps> = ({ cte, isOpen, onClose }) => {
  const baixarXmlMutation = useBaixarXmlCte();
  const [copiado, setCopiado] = useState(false);

  // Fechar com ESC + travar scroll do body enquanto aberto.
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
  }, [isOpen, onClose]);

  const handleCopiarChave = async () => {
    if (!cte?.chaveAcesso) return;
    try {
      await navigator.clipboard.writeText(cte.chaveAcesso);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch { /* silencioso */ }
  };

  const handleBaixarXml = () => { if (cte) baixarXmlMutation.mutate(cte.id); };

  if (!isOpen || !cte) return null;

  const subtitulo = [cte.serie && `Série ${cte.serie}`, formatDateTime(cte.dataEmissao)].filter(Boolean).join(' · ');

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" onClick={onClose} />

      {/* Mobile: full-screen. sm+: card centralizado. */}
      <div className="fixed inset-0 flex flex-col sm:items-center sm:justify-center sm:p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Detalhes do CT-e"
          className="relative flex h-full w-full flex-col overflow-hidden bg-surface sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-iris-lg sm:border sm:border-line-strong sm:shadow-iris-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris bg-accent">
                <DocumentTextIcon className="h-4 w-4 text-white" />
              </span>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold text-tx">CT-e {cte.numero || 'S/N'}</h2>
                <p className="truncate text-[11px] text-tx-3">{subtitulo}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-5">
            {/* Hero — o dinheiro num relance */}
            <div className="flex items-end justify-between gap-3 rounded-iris border border-line bg-surface-2 px-4 py-3">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-tx-3">Valor total</p>
                <p className="text-2xl font-bold tabular-nums text-ok">{money(cte.valorTotal)}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-medium uppercase tracking-wide text-tx-3">Frete</p>
                <p className="text-sm font-semibold tabular-nums text-tx">{money(cte.valorFrete)}</p>
                {cte.tipoPagamento != null && (
                  <p className={`text-[11px] font-medium ${formatTipoFrete(cte.tipoPagamento).color}`}>
                    {formatTipoFrete(cte.tipoPagamento).label}
                  </p>
                )}
              </div>
            </div>

            {/* Documento — linhas rótulo ··· valor */}
            <Grupo titulo="Documento">
              <Linha label="Número" value={cte.numero || 'Não informado'} />
              {cte.serie && <Linha label="Série" value={cte.serie} />}
              <Linha label="Emissão" value={formatDateTime(cte.dataEmissao)} num />
              {cte.tipoServico && <Linha label="Tipo de serviço" value={cte.tipoServico} />}
            </Grupo>

            {/* Partes */}
            <Grupo titulo="Partes">
              <Parte icone={<BuildingOffice2Icon className="h-3.5 w-3.5" />} papel="Emitente" nome={cte.emitenteNome} cnpj={cte.emitenteCnpj} />
              <Parte icone={<TruckIcon className="h-3.5 w-3.5" />} papel="Remetente" nome={cte.remetenteNome} cnpj={cte.remetenteCnpj} />
              <Parte icone={<CubeIcon className="h-3.5 w-3.5" />} papel="Destinatário" nome={cte.destinatarioNome} cnpj={cte.destinatarioCnpj} />
            </Grupo>

            {/* Chave de acesso */}
            {cte.chaveAcesso && (
              <div>
                <div className="mb-1.5 flex items-center justify-between px-1">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-tx-3">Chave de acesso</p>
                  <button
                    onClick={handleCopiarChave}
                    className="inline-flex items-center gap-1.5 rounded-iris-sm px-1.5 py-0.5 text-xs font-medium text-accent-text transition-opacity hover:opacity-80"
                  >
                    {copiado ? <><CheckIcon className="h-3.5 w-3.5" /> Copiada</> : <><DocumentDuplicateIcon className="h-3.5 w-3.5" /> Copiar</>}
                  </button>
                </div>
                <p className="break-all rounded-iris border border-line bg-surface-2 p-3 font-mono text-[13px] text-tx-2">{cte.chaveAcesso}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-line bg-surface-2 px-4 py-3">
            <button onClick={onClose} className={btnSecundario}>Fechar</button>
            <button onClick={handleBaixarXml} disabled={baixarXmlMutation.isPending} className={btnPrimario}>
              <ArrowDownTrayIcon className="h-4 w-4" />
              {baixarXmlMutation.isPending ? 'Baixando…' : 'Baixar XML'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Grupo rotulado com lista de linhas (borda + divisores). */
const Grupo: React.FC<{ titulo: string; children: React.ReactNode }> = ({ titulo, children }) => (
  <div>
    <p className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-tx-3">{titulo}</p>
    <div className="divide-y divide-line rounded-iris border border-line">{children}</div>
  </div>
);

/** Linha rótulo ··· valor (à direita). */
const Linha: React.FC<{ label: string; value: React.ReactNode; num?: boolean }> = ({ label, value, num }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2">
    <span className="text-[13px] text-tx-3">{label}</span>
    <span className={`text-right text-sm font-medium text-tx ${num ? 'tabular-nums' : ''}`}>{value}</span>
  </div>
);

/** Parte (emitente/remetente/destinatário) — papel + nome + CNPJ. */
const Parte: React.FC<{ icone: React.ReactNode; papel: string; nome?: string | null; cnpj?: string | null }> = ({ icone, papel, nome, cnpj }) => (
  <div className="px-3 py-2.5">
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-tx-3">{icone}{papel}</div>
    <p className="mt-0.5 truncate text-sm font-medium text-tx" title={nome ?? undefined}>{nome || 'Não informado'}</p>
    <p className="font-mono text-[13px] tabular-nums text-tx-3">{formatCnpj(cnpj ?? '')}</p>
  </div>
);

export default CteModal;
